import { getCsrfTokenFromDocumentCookie } from "./auth-api"

export type AbsenceRequestState =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"

export type AbsenceRequest = {
  id: string
  memberId: string
  startAt: string
  endAt: string
  reason: string | null
  state: AbsenceRequestState
  reviewedByAdminId: string | null
  reviewedAt: string | null
  createdAt: string
  updatedAt: string
  member: {
    userId: string
    displayName: string | null
  } | null
}

export type MyAbsenceDashboard = {
  reviewRequired: boolean
  activeItems: AbsenceRequest[]
  historyItems: AbsenceRequest[]
}

export type AbsenceAdminOverview = {
  reviewRequired: boolean
  activeAbsences: AbsenceRequest[]
  pendingRequests: AbsenceRequest[]
}

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const body = (await response.json()) as {
      error?: { message?: string }
      message?: string
    }

    return body.error?.message ?? body.message ?? fallback
  } catch {
    return fallback
  }
}

const getCsrfToken = () => {
  const csrfToken = getCsrfTokenFromDocumentCookie()
  if (!csrfToken) {
    throw new Error("Missing CSRF token")
  }

  return csrfToken
}

export const absenceTrackingApi = {
  getMyDashboard: async (): Promise<MyAbsenceDashboard> => {
    const response = await fetch("/api/absence-tracking/me", {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load absence dashboard"),
      )
    }

    return (await response.json()) as MyAbsenceDashboard
  },
  apply: async (input: {
    startAt: string
    endAt: string
    reason?: string | null
  }): Promise<AbsenceRequest> => {
    const response = await fetch("/api/absence-tracking/me", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": getCsrfToken(),
      },
      body: JSON.stringify({
        startAt: input.startAt,
        endAt: input.endAt,
        reason: input.reason ?? null,
      }),
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to submit absence"),
      )
    }

    const payload = (await response.json()) as { item: AbsenceRequest }
    return payload.item
  },
  cancel: async (requestId: string): Promise<AbsenceRequest> => {
    const response = await fetch(
      `/api/absence-tracking/me/${requestId}/cancel`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": getCsrfToken(),
        },
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to cancel absence"),
      )
    }

    const payload = (await response.json()) as { item: AbsenceRequest }
    return payload.item
  },
  getAdminOverview: async (): Promise<AbsenceAdminOverview> => {
    const response = await fetch("/api/absence-tracking/admin", {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load absence overview"),
      )
    }

    return (await response.json()) as AbsenceAdminOverview
  },
  review: async (
    requestId: string,
    decision: "approved" | "rejected",
  ): Promise<AbsenceRequest> => {
    const response = await fetch(
      `/api/absence-tracking/admin/${requestId}/review`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": getCsrfToken(),
        },
        body: JSON.stringify({ decision }),
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to review absence submission"),
      )
    }

    const payload = (await response.json()) as { item: AbsenceRequest }
    return payload.item
  },
}
